import React, { useEffect, useMemo, useState } from 'react';
import { knowledgeBaseMetadataServices } from '../../../APIServices/knowledgeBaseMetadataServices';
import { useToast } from '../../../hooks/useToast';

const METADATA_TYPES = [
  { id: 'knowledgebase_type', name: 'Knowladgebase Type' },
  { id: 'knowledgebase_collection', name: 'Knowladgebase Collection' },
  { id: 'knowledgebase_therapeutic_area', name: 'Knowladgebase Therapeutic Area' },
];

function KnowledgeBaseMetadataManagement() {
  const [activeType, setActiveType] = useState('knowledgebase_type');
  const [rows, setRows] = useState([]);
  const [deletedRows, setDeletedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [form, setForm] = useState({ label: '', value: '' });
  const [editingId, setEditingId] = useState('');
  const [editingForm, setEditingForm] = useState({ label: '', value: '' });
  const toast = useToast();

  const activeLabel = useMemo(
    () => METADATA_TYPES.find((m) => m.id === activeType)?.name || 'Metadata',
    [activeType]
  );

  const loadActive = async () => {
    setLoading(true);
    const data = await knowledgeBaseMetadataServices.getKnowledgeBaseMetadataData('active', activeType);
    setRows(data?.data || []);
    setLoading(false);
  };

  const loadDeleted = async () => {
    const data = await knowledgeBaseMetadataServices.getKnowledgeBaseMetadataData('deleted', activeType);
    setDeletedRows(data?.data || []);
  };

  useEffect(() => {
    loadActive();
    loadDeleted();
    setEditingId('');
    setForm({ label: '', value: '' });
  }, [activeType]);

  const addItem = async () => {
    if (!form.label.trim()) return;

    await knowledgeBaseMetadataServices.addNewKnowledgeBaseMetadata({
      metadataType: activeType,
      label: form.label.trim(),
      value: (form.value || form.label).trim(),
    });

    setForm({ label: '', value: '' });
    await loadActive();
    await loadDeleted();
  };

  const startEdit = (row) => {
    setEditingId(row._id);
    setEditingForm({
      label: row.label || '',
      value: row.value || '',
    });
  };

  const saveEdit = async (row) => {
    try {
      await knowledgeBaseMetadataServices.updateKnowledgeBaseMetadata({
        metadataId: row.metadataId,
        label: editingForm.label,
        value: editingForm.value,
      });

      // Success toast
      toast('Knowledge base metadata updated successfully.', 'ok');

      setEditingId('');
      await loadActive();
    } catch (error) {
      // Error toast
      toast('Unable to update metadata. Please try again.', 'warn');
    }
  };

  const softDelete = async (row) => {
    try {
      await knowledgeBaseMetadataServices.softDeleteRestoreKnowledgeBaseMetadata({
        id: row._id,
        type: 'delete',
      });

      toast('Knowledge base metadata deleted successfully.', 'ok');
      await loadActive();
      await loadDeleted();
    } catch (error) {
      toast('Unable to delete metadata. Please try again.', 'warn');
    }
  };

  const restore = async (row) => {
    try {
      await knowledgeBaseMetadataServices.softDeleteRestoreKnowledgeBaseMetadata({
        id: row._id,
        type: 'restore',
      });

      toast('Knowledge base metadata restored successfully.', 'ok');
      await loadActive();
      await loadDeleted();
    } catch (error) {
      toast('Unable to restore metadata. Please try again.', 'warn');
    }
  };
  const permanentDelete = async (row) => {
    try {
      await knowledgeBaseMetadataServices.deleteKnowledgeBaseMetadata({ id: row._id });
      toast('Knowledge base metadata permanently deleted successfully.', 'ok');
      await loadDeleted();
    } catch (error) {
      toast('Unable to permanently delete metadata. Please try again.', 'warn');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Workspace metadata</div>
          <h1 className="page-title">Knowledgebase Metadata Management</h1>
          <p className="page-sub">
            Admin se Knowladgebase Type, Knowladgebase Collection, aur Knowladgebase Therapeutic Area manage kijiye.
          </p>
        </div>
      </div>

      <div className="card mb-3 card-pad">
        <div className="flex gap-2 wrap" style={{ marginBottom: 12 }}>
          {METADATA_TYPES.map((item) => (
            <button
              key={item.id}
              className={`btn btn-sm ${activeType === item.id ? 'btn-primary' : ''}`}
              onClick={() => setActiveType(item.id)}
            >
              {item.name}
            </button>
          ))}
        </div>

        <div className="field-row">
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Label</label>
            <input
              value={form.label}
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
              placeholder={`Add ${activeLabel} label`}
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Value (optional)</label>
            <input
              value={form.value}
              onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))}
              placeholder="Machine value (default = label)"
            />
          </div>
        </div>
        <button className="btn btn-primary" onClick={addItem}>
          <i className="ti ti-plus" /> Add
        </button>
      </div>

      <div className="flex between center mb-3">
        <div className="section-label">
          {activeLabel} <span className="count">{rows.length}</span>
        </div>
        <button className="btn btn-danger" onClick={() => setShowDeleted((s) => !s)}>
          <i className="ti ti-history" /> {showDeleted ? 'Hide' : 'Show'} Deleted History
        </button>
      </div>

      <div className="card mb-3">
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div className="buttonLoader" />
            <div style={{ marginTop: 10 }}>Loading metadata...</div>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
            No active metadata for this category.
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Label</th>
                <th>Value</th>
                <th style={{ width: 130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row._id}>
                  <td>
                    {editingId === row._id ? (
                      <input
                        value={editingForm.label}
                        onChange={(e) => setEditingForm((p) => ({ ...p, label: e.target.value }))}
                      />
                    ) : (
                      <span className="medium">{row.label}</span>
                    )}
                  </td>
                  <td>
                    {editingId === row._id ? (
                      <input
                        value={editingForm.value}
                        onChange={(e) => setEditingForm((p) => ({ ...p, value: e.target.value }))}
                      />
                    ) : (
                      <span className="xs">{row.value}</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      {editingId === row._id ? (
                        <>
                          <button className="icon-btn" title="Save" style={{ color: 'var(--ok)' }} onClick={() => saveEdit(row)}>
                            <i className="ti ti-check" />
                          </button>
                          <button className="icon-btn" title="Cancel" onClick={() => setEditingId('')}>
                            <i className="ti ti-x" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="icon-btn" title="Edit" style={{ color: 'var(--info)' }} onClick={() => startEdit(row)}>
                            <i className="ti ti-edit" />
                          </button>
                          <button className="icon-btn" title="Delete" style={{ color: 'var(--danger)' }} onClick={() => softDelete(row)}>
                            <i className="ti ti-trash" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showDeleted && (
        <>
          <div className="section-label mb-2">Deleted History ({deletedRows.length})</div>
          <div className="card mt-3 ">
            {deletedRows.length === 0 ? (
              <div className="xs muted card-pad">No deleted records.</div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Value</th>
                    <th>Deleted On</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedRows.map((row) => (
                    <tr key={row._id}>
                      <td className="medium">{row.label}</td>
                      <td className="xs">{row.value}</td>
                      <td className="xs">{row.deletedDT ? new Date(row.deletedDT).toLocaleString() : '-'}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-xs" onClick={() => restore(row)}>
                            Restore
                          </button>
                          <button className="btn btn-xs btn-danger" onClick={() => permanentDelete(row)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <style>{`
        .wrap { flex-wrap: wrap; }
        .count {
          background: var(--surface-2);
          padding: 2px 10px;
          border-radius: 20px;
          font-size: 12px;
          margin-left: 6px;
        }
        .mb-3 { margin-bottom: 12px; }
        .mt-3 { margin-top: 12px; }
        .mb-2 { margin-bottom: 8px; }
        .center { align-items: center; }
        .between { justify-content: space-between; }
        .icon-btn {
          background: transparent;
          border: none;
          padding: 4px 6px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
        }
        .icon-btn:hover { background: var(--surface-2); }
      `}</style>
    </div>
  );
}

export default KnowledgeBaseMetadataManagement;
